import React, { useState, useRef, useEffect } from 'react';

function AddressField({
  address,
  onAddressChange,
  onAddressSelect,
  MAPBOX_TOKEN,
}) {
  const [addressSuggestions, setAddressSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isLoadingSuggestions, setIsLoadingSuggestions] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const suggestionTimeoutRef = useRef(null);
  const suggestionsRef = useRef([]);

  const fetchAddressSuggestions = async (query) => {
    if (!query || query.length < 3) {
      setAddressSuggestions([]);
      setIsLoadingSuggestions(false);
      return;
    }

    setIsLoadingSuggestions(true);

    try {
      const response = await fetch(
        `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(
          query
        )}.json?country=GB&access_token=${MAPBOX_TOKEN}&limit=5`
      );
      const data = await response.json();

      if (data.features) {
        setAddressSuggestions(data.features);

        let backgroundSelection = {
          place_name: query,
          center: data.features[0].center,
        };

        if (data.features.length > 0) {
          handleAddressSelect(backgroundSelection, true);
        }
      }
    } catch (error) {
      setAddressSuggestions([]);
    }

    setIsLoadingSuggestions(false);
  };

  const handleAddressChange = (value) => {
    onAddressChange(value);

    // Clear existing timeout
    if (suggestionTimeoutRef.current) {
      clearTimeout(suggestionTimeoutRef.current);
    }

    suggestionTimeoutRef.current = setTimeout(() => {
      fetchAddressSuggestions(value);
    }, 300);

    setShowSuggestions(true);
    setFocusedIndex(-1);
  };

  const handleAddressSelect = (suggestion, isBackgroundSelection = false) => {
    const coordinates = {
      lng: suggestion.center[0],
      lat: suggestion.center[1],
    };

    onAddressSelect(suggestion.place_name, coordinates);

    if (!isBackgroundSelection) {
      setAddressSuggestions([]);
      setShowSuggestions(false);
      setFocusedIndex(-1);
    }
  };

  const handleFocus = () => {
    setShowSuggestions(true);
  };

  const handleInputKeyDown = (e) => {
    if (!showSuggestions || addressSuggestions.length === 0) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setFocusedIndex((prev) =>
        prev < addressSuggestions.length - 1 ? prev + 1 : 0
      );
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setFocusedIndex((prev) =>
        prev > 0 ? prev - 1 : addressSuggestions.length - 1
      );
    } else if (e.key === 'Enter') {
      if (focusedIndex !== -1) {
        e.preventDefault();
        handleAddressSelect(addressSuggestions[focusedIndex]);
      }
    } else if (e.key === 'Escape') {
      setShowSuggestions(false);
      setFocusedIndex(-1);
    }
    // Tab key is not handled - allows normal tab navigation to next field
  };

  const containerRef = useRef(null);
  const suggestionRefs = useRef([]);

  // Scroll focused suggestion into view
  useEffect(() => {
    if (focusedIndex !== -1 && suggestionRefs.current[focusedIndex]) {
      suggestionRefs.current[focusedIndex].scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
      });
    }
  }, [focusedIndex]);

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target)
      ) {
        setShowSuggestions(false);
        setFocusedIndex(-1);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleInputBlur = () => {
    setShowSuggestions(false);
    setFocusedIndex(-1);
  };

  return (
    <div
      className="relative flex-1 lg:border-b lg:border-[#ebebeb] xl:border-none"
      ref={containerRef}
    >
      {/* Address suggestions dropdown - moved before input for correct tab order */}
      {showSuggestions && addressSuggestions.length > 0 && (
        <div
          className="absolute top-full left-0 right-0 bg-white border-2 border-[#EBEBEB] z-50 max-h-64 overflow-y-auto"
          role="listbox"
          aria-label="Address suggestions"
        >
          {addressSuggestions.map((suggestion, idx) => (
            <button
              key={suggestion.id}
              id={`suggestion-${idx}`}
              type="button"
              className={`w-full px-7 py-4 text-left hover:bg-gray-100 focus:bg-gray-100 border-b border-[#EBEBEB] last:border-b-0 hover:bg-blue-700 group ${
                focusedIndex === idx ? 'bg-blue-700 text-white' : ''
              }`}
              onClick={() => handleAddressSelect(suggestion)}
              onMouseDown={(e) => e.preventDefault()}
              ref={(el) => (suggestionRefs.current[idx] = el)}
              tabIndex={-1}
              role="option"
              aria-selected={focusedIndex === idx}
            >
              {(() => {
                const parts = suggestion.place_name.split(',');
                if (parts.length > 1) {
                  return (
                    <div className="text-sm">
                      <div
                        className={`font-medium mb-2 group-hover:text-white ${
                          focusedIndex === idx ? 'text-white' : 'text-black'
                        }`}
                      >
                        {parts[0].trim()}
                      </div>
                      <div
                        className={`font-light group-hover:text-white ${
                          focusedIndex === idx ? 'text-white' : 'text-[#6B7280]'
                        }`}
                      >
                        {parts.slice(1).join(',').trim()}
                      </div>
                    </div>
                  );
                }
                return suggestion.place_name;
              })()}
            </button>
          ))}
        </div>
      )}

      {/* Loading suggestions indicator */}
      {showSuggestions &&
        isLoadingSuggestions &&
        addressSuggestions.length === 0 && (
          <div className="absolute top-full left-0 right-0 bg-white border-2 border-[#EBEBEB] z-50 px-4 py-2">
            <div className="text-gray-500">Loading suggestions...</div>
          </div>
        )}

      <input
        type="text"
        name="s"
        className="flex-grow w-full pl-6 pr-12 placeholder:text-black placeholder:text-sm placeholder:font-medium h-15 lg:h-23 border-2 border-blue-700 lg:border-none"
        aria-label="Enter postcode or location"
        placeholder="Enter postcode or location"
        value={address}
        onChange={(e) => handleAddressChange(e.target.value)}
        onFocus={handleFocus}
        onBlur={handleInputBlur}
        onKeyDown={handleInputKeyDown}
        data-1p-ignore="true"
        data-lpignore="true"
        autoComplete="off"
        role="combobox"
        aria-expanded={showSuggestions}
        aria-activedescendant={
          focusedIndex !== -1 ? `suggestion-${focusedIndex}` : undefined
        }
        aria-haspopup="listbox"
      />
    </div>
  );
}

export default AddressField;
